import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import toast from 'react-hot-toast';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { useEscriturasAdicionales } from '../hooks/useEscriturasAdicionales';
import { EscrituraAdicionalUploadForm } from './EscrituraAdicionalUploadForm';
import { EscrituraAdicionalItem } from './EscrituraAdicionalItem';
import type { EscrituraAdicional } from '../types';

const MAX_ESCRITURAS = 100;

interface EscriturasDialogProps {
  tramiteId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EscriturasDialog: React.FC<EscriturasDialogProps> = ({
  tramiteId,
  open,
  onOpenChange,
}) => {
  const { canUploadEscritura } = useAuthorization();

  const {
    escrituras,
    total,
    isLoading,
    isUploading,
    uploadEscritura,
    deleteEscritura,
    downloadEscritura,
  } = useEscriturasAdicionales({ tramiteId });

  const isAtLimit = total >= MAX_ESCRITURAS;

  const handleUpload = async (file: File, descripcion: string) => {
    try {
      await uploadEscritura(file, descripcion);
      toast.success('Escritura subida correctamente');
    } catch (err: any) {
      const backendErrors = err?.response?.data?.errors;
      const backendMessage =
        (backendErrors && (Object.values(backendErrors)[0] as string[] | undefined)?.[0]) ||
        err?.response?.data?.message;

      toast.error(backendMessage || err.message || 'Error al subir la escritura');
      throw err;
    }
  };

  const handleDelete = async (escritura: EscrituraAdicional) => {
    try {
      await deleteEscritura(escritura.id);
      toast.success('Escritura eliminada correctamente');
    } catch {
      toast.error('Error al eliminar la escritura');
      throw new Error('delete failed');
    }
  };

  const handleDownload = async (escritura: EscrituraAdicional) => {
    try {
      await downloadEscritura(escritura);
    } catch {
      toast.error('Error al descargar la escritura');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Escrituras adicionales</DialogTitle>
          <DialogDescription>
            Gestioná las escrituras adicionales del trámite.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {canUploadEscritura && (
            <EscrituraAdicionalUploadForm
              onUpload={handleUpload}
              isUploading={isUploading}
              isAtLimit={isAtLimit}
            />
          )}

          {total > 0 && (
            <div className="flex justify-end">
              <span className="text-xs text-gray-500">{total}/{MAX_ESCRITURAS}</span>
            </div>
          )}

          {isLoading ? (
            <p className="text-sm text-gray-500 text-center py-4">Cargando...</p>
          ) : escrituras.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              No hay escrituras adicionales
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {escrituras.map((escritura) => (
                <EscrituraAdicionalItem
                  key={escritura.id}
                  escritura={escritura}
                  canUploadEscritura={canUploadEscritura}
                  onDelete={handleDelete}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
